/**
 * Payment Required Provider Component
 * 
 * Provides payment required context and state management for the entire application.
 * Handles HTTP 402 (Payment Required) responses and modal visibility.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { PaymentRequiredContext, DEFAULT_PAYMENT_REQUIRED_STATE } from '../context/PaymentRequiredContext';
import PaymentRequiredModal from '../components/PaymentRequiredModal';

/**
 * Payment Required Provider Component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Payment required provider component
 */
export const PaymentRequiredProvider = ({ children }) => {
  const [state, setState] = useState(DEFAULT_PAYMENT_REQUIRED_STATE);

  /**
   * Show payment required modal
   * @param {string} featureName - Name of the feature that requires subscription
   */
  const showPaymentRequired = useCallback((featureName = '') => {
    setState(prevState => ({
      ...prevState,
      isOpen: true,
      featureName,
      error: null
    }));
  }, []);

  /**
   * Hide payment required modal
   */
  const hidePaymentRequired = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isOpen: false,
      featureName: '',
      error: null
    }));
  }, []);

  /**
   * Set error state
   * @param {string|null} error - Error message
   */
  const setError = useCallback((error) => {
    setState(prevState => ({
      ...prevState,
      error
    }));
  }, []);

  /**
   * Handle subscription activation success
   * @param {Object} data - Activation data
   */
  const handleActivationSuccess = useCallback((data) => {
    // Hide the modal after successful activation
    hidePaymentRequired();
    
    // You can add additional logic here, such as:
    // - Showing a success message
    // - Redirecting to a specific page
    // - Refreshing data
  }, [hidePaymentRequired]);

  /**
   * Listen for subscription required events from API interceptors
   */
  useEffect(() => {
    const handleSubscriptionRequired = (event) => {
      const { feature, endpoint, error } = event.detail;
      
      // Extract feature name from endpoint if not provided
      let featureName = feature;
      if (!featureName && endpoint) {
        // Extract feature name from endpoint
        if (endpoint.includes('/markets-analysis')) {
          featureName = 'арбитражни възможности';
        } else if (endpoint.includes('/calculator')) {
          featureName = 'калкулатор за залози';
        } else if (endpoint.includes('/premium')) {
          featureName = 'премиум функции';
        } else {
          featureName = 'тази функция';
        }
      }
      
      // Show modal with feature name
      if (featureName || endpoint) {
        showPaymentRequired(featureName || '');
      }
    };

    // Listen for subscription required events
    window.addEventListener('subscription:required', handleSubscriptionRequired);

    return () => {
      window.removeEventListener('subscription:required', handleSubscriptionRequired);
    };
  }, [showPaymentRequired]);

  /**
   * Context value
   */
  const contextValue = useMemo(() => ({
    // State
    isOpen: state.isOpen,
    featureName: state.featureName,
    error: state.error,
    
    // Actions
    showPaymentRequired,
    hidePaymentRequired,
    setError
  }), [state.isOpen, state.featureName, state.error, showPaymentRequired, hidePaymentRequired, setError]);

  return (
    <PaymentRequiredContext.Provider value={contextValue}>
      {children}
      
      {/* Payment Required Modal */}
      <PaymentRequiredModal
        isOpen={state.isOpen}
        onClose={hidePaymentRequired}
        onActivate={handleActivationSuccess}
        featureName={state.featureName}
      />
    </PaymentRequiredContext.Provider>
  );
};

export default PaymentRequiredProvider;
